import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminMfaGuard from "@/components/AdminMfaGuard";
import FloatingHelpButton from "@/components/FloatingHelpButton";
import AnalyticsTracker from "@/components/AnalyticsTracker";

// Pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import SchoolSetup from "./pages/SchoolSetup";
import Upload from "./pages/Upload";
import Assessments from "./pages/Assessments";
import Pupils from "./pages/Pupils";
import Progress from "./pages/Progress";
import Settings from "./pages/Settings";
import MyClasses from "./pages/MyClasses";
import ChoosePlan from "./pages/ChoosePlan";
import Pricing from "./pages/Pricing";
import IndividualBilling from "./pages/IndividualBilling";
import SchoolBilling from "./pages/SchoolBilling";
import ResetPassword from "./pages/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DataProtection from "./pages/DataProtection";
import CookiePolicy from "./pages/CookiePolicy";
import Safeguarding from "./pages/Safeguarding";
import Contact from "./pages/Contact";
import Help from "./pages/Help";
import TryFree from "./pages/TryFree";
import Unsubscribe from "./pages/Unsubscribe";
import TrustDashboard from "./pages/TrustDashboard";
import AdminSchools from "./pages/admin/AdminSchools";
import AdminSchoolDetail from "./pages/admin/AdminSchoolDetail";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminAssessments from "./pages/admin/AdminAssessments";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminLeads from "./pages/admin/AdminLeads";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AnalyticsTracker />
          <FloatingHelpButton />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/safeguarding" element={<Safeguarding />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/help" element={<Help />} />
            <Route path="/try" element={<TryFree />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            
            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/school-setup"
              element={
                // No requiredRoles: a new school admin has no role until setup completes.
                // SchoolSetup does its own access check (bootstrap vs. existing admin).
                <ProtectedRoute>
                  <SchoolSetup />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/pupils"
              element={
                <ProtectedRoute>
                  <Pupils />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute>
                  <Upload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments"
              element={
                <ProtectedRoute>
                  <Assessments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/progress"
              element={
                <ProtectedRoute>
                  <Progress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-classes"
              element={
                <ProtectedRoute requiredRoles={['school_admin']}>
                  <MyClasses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/choose-plan"
              element={
                <ProtectedRoute>
                  <ChoosePlan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/individual-billing"
              element={
                <ProtectedRoute>
                  <IndividualBilling />
                </ProtectedRoute>
              }
            />
            <Route
              path="/school-billing"
              element={
                <ProtectedRoute>
                  <SchoolBilling />
                </ProtectedRoute>
              }
            />

            <Route
              path="/trust-dashboard"
              element={
                <ProtectedRoute requiredRoles={['trust_leader']}>
                  <TrustDashboard />
                </ProtectedRoute>
              }
            />

            {/* Super admin routes — require super_admin role AND MFA AAL2 */}
            <Route
              path="/admin/schools"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminSchools />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/schools/:schoolId"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminSchoolDetail />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminUsers />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminUserDetail />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assessments"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminAssessments />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminAuditLog />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminAnalytics />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <AdminMfaGuard>
                    <AdminLeads />
                  </AdminMfaGuard>
                </ProtectedRoute>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
